// src/api/services.js
// Todos os servizos da API de Leyro

import { api } from "./client";

// ═══════════════════════════════════════════════════════════════════════════
// AUTH
// ═══════════════════════════════════════════════════════════════════════════
export const authService = {
  register: (data) => api.post("/auth/register", data),
  // X-3.2 / X-4.1 — login con Google idToken
  googleSignIn: (idToken) => api.post("/auth/google", { idToken }),
  login: (email, password) => api.post("/auth/login", { email, password }),
  me: () => api.get("/auth/me"),
  resendVerification: (email) => api.post("/auth/resend-verification", { email }),
  // Persistir idioma da UI no perfil (require sesión iniciada).
  updateLanguage: (language) => api.patch("/auth/me/language", { language }),
  // 2FA (TOTP)
  setup2FA: () => api.post("/auth/2fa/setup"),
  verify2FA: (token) => api.post("/auth/2fa/verify", { token }),
  disable2FA: (token) => api.post("/auth/2fa/disable", { token }),
  twoFactorStatus: () => api.get("/auth/2fa/status"),
  // Sesión elevada (para entrar no panel admin)
  elevate2FA: (token) => api.post("/auth/2fa/elevate", { token }),
  elevationStatus: () => api.get("/auth/2fa/elevation-status"),
};

// ═══════════════════════════════════════════════════════════════════════════
// MÁQUINAS
// ═══════════════════════════════════════════════════════════════════════════
export const machineService = {
  // Buscar con filtros
  search: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== "" && v !== "all" && v !== "Todas"))
    ).toString();
    return api.get(`/machines${qs ? `?${qs}` : ""}`);
  },

  // Detalle dunha máquina
  get: (id) => api.get(`/machines/${id}`),

  // Publicar nova máquina
  create: (data) => api.post("/machines", data),

  // Editar
  update: (id, data) => api.put(`/machines/${id}`, data),

  // Eliminar (soft delete)
  delete: (id) => api.delete(`/machines/${id}`),

  // As miñas máquinas
  mine: () => api.get("/machines?mine=true"),

  // Bloqueos de datas (propietario)
  getBlocks: (id) => api.get(`/machines/${id}/blocks`),
  addBlock: (id, dateFrom, dateTo, reason) =>
    api.post(`/machines/${id}/blocks`, { dateFrom, dateTo, reason }),
  deleteBlock: (machineId, blockId) =>
    api.delete(`/machines/${machineId}/blocks/${blockId}`),
};

// ═══════════════════════════════════════════════════════════════════════════
// RESERVAS
// ═══════════════════════════════════════════════════════════════════════════
export const reservationService = {
  // Crear reserva
  create: (data) => api.post("/reservations", data),

  // As miñas reservas (como arrendatario)
  mine: () => api.get("/reservations"),

  // Reservas recibidas (como propietario)
  received: () => api.get("/reservations/received"),

  // Detalle
  get: (id) => api.get(`/reservations/${id}`),

  // Confirmar recollida con fotos
  confirmPickup: (id, photoUrls) =>
    api.post(`/reservations/${id}/confirm-pickup`, { photoUrls }),

  // Confirmar devolución
  confirmReturn: (id, photoUrls, noIncidents) =>
    api.post(`/reservations/${id}/confirm-return`, { photoUrls, noIncidents }),

  // Cancelar
  cancel: (id) => api.delete(`/reservations/${id}`),

  // Aprobar / rexeitar solicitude (propietario)
  approve: (id) => api.post(`/reservations/${id}/approve`, {}),
  reject: (id) => api.post(`/reservations/${id}/reject`, {}),
};

// ═══════════════════════════════════════════════════════════════════════════
// PAGOS (STRIPE)
// ═══════════════════════════════════════════════════════════════════════════
export const paymentService = {
  // Iniciar onboarding Stripe Connect (propietarios)
  startOnboarding: () => api.post("/payments/connect/onboard"),

  // Estado da conta bancaria
  getAccountStatus: () => api.get("/payments/connect/status"),

  // X-0.F · PaymentElement: clientSecret dun PaymentIntent novo
  // Body: { machineId, dateFrom, dateTo, deliveryPrice? }
  // Devolve: { clientSecret, paymentIntentId, amounts }
  createIntent: (body) => api.post("/payments/intent", body),

  // X-0.F · PaymentElement modo setup: clientSecret dun SetupIntent
  // (para gardar tarxeta sen cobrar — cobros anuais finca)
  createSetupIntent: (body = {}) => api.post("/payments/setup-intent", body),

  // X-0.F PR4 · Tarxetas gardadas
  listMethods: () => api.get("/payments/methods"),
  detachMethod: (pmId) => api.delete(`/payments/methods/${pmId}`),
};

// ═══════════════════════════════════════════════════════════════════════════
// CONTRATOS
// ═══════════════════════════════════════════════════════════════════════════
export const contractService = {
  // Estado do contrato dunha reserva
  getStatus: (reservationId) => api.get(`/contracts/${reservationId}`),

  // Datos completos para previsualizar o contrato
  preview: (reservationId) => api.get(`/contracts/${reservationId}/preview`),

  // Asinar en modo demo
  demoSign: (reservationId) => api.post(`/contracts/${reservationId}/demo-sign`),

  // Reenviar por email
  resend: (reservationId) => api.post(`/contracts/${reservationId}/resend`),
};

// ═══════════════════════════════════════════════════════════════════════════
// VERIFICACIÓN DE IDENTIDADE
// ═══════════════════════════════════════════════════════════════════════════
export const identityService = {
  // Iniciar sesión de verificación DNI
  start: () => api.post("/identity/start"),

  // Comprobar resultado
  getStatus: () => api.get("/identity/status"),
};

// ═══════════════════════════════════════════════════════════════════════════
// RESEÑAS
// ═══════════════════════════════════════════════════════════════════════════
export const reviewService = {
  // Escribir reseña
  create: (data) => api.post("/reviews", data),

  // Reseñas dunha máquina
  forMachine: (machineId) => api.get(`/reviews/machine/${machineId}`),

  // Reseñas dun usuario
  forUser: (userId, type) =>
    api.get(`/reviews/user/${userId}${type ? `?type=${type}` : ""}`),

  // Reseñas pendentes de escribir
  pending: () => api.get("/reviews/pending"),
};

// ═══════════════════════════════════════════════════════════════════════════
// SUBIDA DE FOTOS
// ═══════════════════════════════════════════════════════════════════════════
export const uploadService = {
  // Fotos dunha máquina
  machinePhotos: (machineId, files, labels = []) => {
    const form = new FormData();
    files.forEach((f, i) => {
      form.append("photos", f);
      if (labels[i]) form.append("labels[]", labels[i]);
    });
    return api.upload(`/upload/machine/${machineId}`, form);
  },

  // Fotos dunha finca
  fincaPhotos: (fincaId, files) => {
    const form = new FormData();
    files.forEach(f => form.append("photos", f));
    return api.upload(`/upload/finca/${fincaId}`, form);
  },

  // Fotos de recollida ou devolución (con GPS opcional)
  reservationPhotos: (reservationId, type, files, gps = {}) => {
    const form = new FormData();
    files.forEach(f => form.append("photos", f));
    if (gps.latitude  != null) form.append("latitude",  gps.latitude);
    if (gps.longitude != null) form.append("longitude", gps.longitude);
    if (gps.accuracy  != null) form.append("accuracy",  gps.accuracy);
    return api.upload(`/upload/reservation/${reservationId}/${type}`, form);
  },

  // Fotos de evidencia para incidencias (retorna URLs, sen BD propia)
  evidencePhotos: (files) => {
    const form = new FormData();
    files.forEach(f => form.append("photos", f));
    return api.upload("/upload/evidence", form);
  },

  // Foto de perfil
  avatar: (file) => {
    const form = new FormData();
    form.append("avatar", file);
    return api.upload("/upload/avatar", form);
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// TERRAS
// ═══════════════════════════════════════════════════════════════════════════
export const terrasService = {
  // Buscar fincas con filtros
  search: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== "" && v !== "Todas"))
    ).toString();
    return api.get(`/terras${qs ? `?${qs}` : ""}`);
  },

  // Detalle dunha finca
  get: (id) => api.get(`/terras/${id}`),

  // Publicar finca
  create: (data) => api.post("/terras", data),

  // Editar
  update: (id, data) => api.put(`/terras/${id}`, data),

  // Eliminar (soft delete)
  delete: (id) => api.delete(`/terras/${id}`),

  // As miñas fincas
  mine: () => api.get("/terras/mis-fincas"),

  // Os meus arrendamentos (como arrendatario)
  myLeases: () => api.get("/terras/mis-arrendamentos"),

  // Reservas de fincas recibidas (como propietario)
  receivedLeases: () => api.get("/terras/reservas-recibidas"),

  // Cancelar reserva de finca
  cancelLease: (id) => api.delete(`/terras/reservas/${id}`),

  // Aprobar / rexeitar solicitude de finca (como propietario)
  approveLease: (id) => api.post(`/terras/reservas/${id}/approve`),
  rejectLease:  (id) => api.post(`/terras/reservas/${id}/reject`),

  // Actualizar método de pago gardado para cargos anuais
  updatePayment: (id, paymentMethodId) => api.post(`/terras/reservas/${id}/update-payment`, { paymentMethodId }),

  // Reservar finca
  reserve: (id, data) => api.post(`/terras/${id}/reservar`, data),

  // Contrato de arrendamento
  reservaContract: (reservaId) => api.get(`/terras/reservas/${reservaId}/contract`),
  reservaDemoSign: (reservaId) => api.post(`/terras/reservas/${reservaId}/demo-sign`),

  // Buscar parcela SIGPAC (deprecated — FEGA WFS pechado)
  sigpacSearch: (params) => {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/terras/sigpac/buscar?${qs}`);
  },

  // Buscar parcela en Catastro (lat/lng ou RC)
  catastroSearch: (params) => {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/terras/catastro/buscar?${qs}`);
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// CHAT
// ═══════════════════════════════════════════════════════════════════════════
export const chatService = {
  // Listar todas as conversas
  list: () => api.get("/chat"),

  // Crear ou obter conversa existente
  // payload: { machineId, recipientId } | { fincaId, recipientId }
  getOrCreate: (payload) => api.post("/chat", payload),

  // Mensaxes dunha conversa
  messages: (conversationId, before) =>
    api.get(`/chat/${conversationId}/messages${before ? `?before=${before}` : ""}`),

  // Enviar mensaxe
  send: (conversationId, text) =>
    api.post(`/chat/${conversationId}/messages`, { text }),

  // Marcar como lida
  markRead: (conversationId) =>
    api.put(`/chat/${conversationId}/read`, {}),
};

// ═══════════════════════════════════════════════════════════════════════════
// FAVORITOS
// ═══════════════════════════════════════════════════════════════════════════
export const favoriteService = {
  // Lista de favoritos (máquinas + fincas)
  list: () => api.get("/favorites"),

  // IDs de favoritos (para mostrar estado ❤️ en tarxetas)
  ids: () => api.get("/favorites/ids"),

  // Toggle máquina favorita
  toggleMachine: (id) => api.post(`/favorites/machine/${id}`, {}),

  // Toggle finca favorita
  toggleFinca: (id) => api.post(`/favorites/finca/${id}`, {}),
};

// ═══════════════════════════════════════════════════════════════════════════
// DEVICES (X-3.1)
// ═══════════════════════════════════════════════════════════════════════════
export const deviceService = {
  // Listar dispositivos do usuario actual
  listMine: () => api.get("/devices/me"),

  // Rexistrar / actualizar device (usado por mobile + web push permission)
  register: ({ fcmToken, platform, deviceName }) =>
    api.post("/devices", { fcmToken, platform, deviceName }),

  // Logout dun device concreto
  remove: (id) => api.delete(`/devices/${id}`),
};

// ═══════════════════════════════════════════════════════════════════════════
// NOTIFICACIÓNS
// ═══════════════════════════════════════════════════════════════════════════
export const notificationService = {
  list: (unreadOnly = false) =>
    api.get(`/notifications${unreadOnly ? "?unreadOnly=true" : ""}`),

  markRead: (id) => api.put(`/notifications/${id}/read`, {}),

  markAllRead: () => api.put("/notifications/read-all", {}),

  delete: (id) => api.delete(`/notifications/${id}`),
};

// ═══════════════════════════════════════════════════════════════════════════
// MARKETPLACE (busca unificada)
// ═══════════════════════════════════════════════════════════════════════════
export const marketplaceService = {
  search: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ""))
    ).toString();
    return api.get(`/marketplace/search${qs ? `?${qs}` : ""}`);
  },

  stats: () => api.get("/marketplace/stats"),
};

// ═══════════════════════════════════════════════════════════════════════════
// INCIDENCIAS
// ═══════════════════════════════════════════════════════════════════════════
export const incidentService = {
  // Crear incidencia
  create: (data) => api.post("/incidents", data),

  // Listar as miñas incidencias
  list: () => api.get("/incidents"),

  // Detalle
  get: (id) => api.get(`/incidents/${id}`),

  // Actualizar
  update: (id, data) => api.put(`/incidents/${id}`, data),

  // Resolver (árbitro/admin)
  resolve: (id, resolution) => api.post(`/incidents/${id}/resolve`, { resolution }),

  // Escalar
  escalate: (id) => api.post(`/incidents/${id}/escalate`, {}),
};

// ═══════════════════════════════════════════════════════════════════════════
// TRANSACCIÓNS
// ═══════════════════════════════════════════════════════════════════════════
export const transactionService = {
  // Historial
  list: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ""))
    ).toString();
    return api.get(`/transactions${qs ? `?${qs}` : ""}`);
  },

  // Resumo
  summary: () => api.get("/transactions/summary"),
};

// ═══════════════════════════════════════════════════════════════════════════
// GALERÍA, MANTEMENTO, FICHA TÉCNICA, LOXÍSTICA
// ═══════════════════════════════════════════════════════════════════════════
export const machineExtrasService = {
  // Galería
  reorderPhotos:  (machineId, order)          => api.put(`/machines/${machineId}/photos/order`, { order }),
  deletePhoto:    (machineId, photoId)         => api.delete(`/machines/${machineId}/photos/${photoId}`),
  labelPhoto:     (machineId, photoId, label)  => api.put(`/machines/${machineId}/photos/${photoId}/label`, { label }),

  // Mantemento
  getMaintenance: (machineId)                  => api.get(`/machines/${machineId}/maintenance`),
  addMaintenance: (machineId, data)            => api.post(`/machines/${machineId}/maintenance`, data),
  deleteMaintenance: (machineId, logId)        => api.delete(`/machines/${machineId}/maintenance/${logId}`),

  // Ficha técnica
  getSpec:        (machineId)                  => api.get(`/machines/${machineId}/spec`),
  updateSpec:     (machineId, data)            => api.put(`/machines/${machineId}/spec`, data),

  // Loxística
  requestLogistics: (machineId, data)          => api.post(`/machines/${machineId}/logistics`, data),
};

// ═══════════════════════════════════════════════════════════════════════════
// ANALYTICS
// ═══════════════════════════════════════════════════════════════════════════
export const analyticsService = {
  owner:  (months = 12) => api.get(`/analytics/owner?months=${months}`),
  fincas: (months = 24) => api.get(`/analytics/fincas?months=${months}`),
};

// ═══════════════════════════════════════════════════════════════════════════
// PREZOS ESTACIONAIS
// ═══════════════════════════════════════════════════════════════════════════
export const seasonalPricingService = {
  list:   (machineId)              => api.get(`/machines/${machineId}/pricing`),
  create: (machineId, data)        => api.post(`/machines/${machineId}/pricing`, data),
  update: (machineId, priceId, data) => api.put(`/machines/${machineId}/pricing/${priceId}`, data),
  delete: (machineId, priceId)     => api.delete(`/machines/${machineId}/pricing/${priceId}`),
};

// ═══════════════════════════════════════════════════════════════════════════
// DECLARACIÓNS RESPONSABLES (legal)
// ═══════════════════════════════════════════════════════════════════════════
export const legalService = {
  // context ∈ publish_machine | rent_machine | publish_finca | rent_finca
  getDeclaration: (context) => api.get(`/legal/declaration/${context}`),
};

// ═══════════════════════════════════════════════════════════════════════════
// ADMIN
// ═══════════════════════════════════════════════════════════════════════════
export const adminService = {
  dashboard: () => api.get("/admin/dashboard"),
  users: (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ""))).toString();
    return api.get(`/admin/users${qs ? `?${qs}` : ""}`);
  },
  verifyRopo: (userId, verified) => api.put(`/admin/users/${userId}/ropo`, { verified }),
  incidents: (status = "OPEN") => api.get(`/admin/incidents?status=${status}`),
  resolveIncident: (id, resolution, status) => api.put(`/admin/incidents/${id}/resolve`, { resolution, status }),
  health: () => api.get("/admin/health"),
  consistency: () => api.get("/admin/health/consistency"),
  quotas: () => api.get("/admin/quotas"),
  // Settings
  getSettings: () => api.get("/admin/settings"),
  updateSettings: (settings) => api.put("/admin/settings", { settings }),

  // X-0.E · Toggle STRIPE_BYPASS_CONNECT en runtime
  getStripeBypass: () => api.get("/admin/settings/stripe-bypass"),
  setStripeBypass: (active) => api.put("/admin/settings/stripe-bypass", { active }),
  // Block/unblock users
  blockUser: (userId, reason) => api.post(`/admin/users/${userId}/block`, { reason }),
  unblockUser: (userId) => api.post(`/admin/users/${userId}/unblock`, {}),

  // Reports (denuncias de anuncios)
  reports: (status = "PENDING") => api.get(`/admin/reports?status=${status}`),
  updateReport: (id, payload) => api.put(`/admin/reports/${id}`, payload),

  // Axudas — leads recibidos por /axudas
  axudasLeads: (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v != null && v !== "")
    )).toString();
    return api.get(`/admin/axudas/leads${qs ? `?${qs}` : ""}`);
  },
  updateAxudasLead: (id, payload) => api.put(`/admin/axudas/leads/${id}`, payload),

  // Estado do sistema e servizos externos
  systemStatus: () => api.get("/admin/system/status"),
  validateStripeKeys: (secretKey, publishableKey) =>
    api.post("/admin/system/stripe/validate", { secretKey, publishableKey }),

  // Backups (S3) — lista é libre; download e restore requiren 2FA
  listBackups: () => api.get("/admin/backups"),
  backupsHealth: () => api.get("/admin/backups/health"),
  getBackupDownloadUrl: (key, twoFactorCode) =>
    api.post("/admin/backups/download-url", { key }, {
      headers: { "X-2FA-Code": twoFactorCode },
    }),
  restoreBackup: (key, confirmName, twoFactorCode) =>
    api.post("/admin/backups/restore", { key, confirmName }, {
      headers: { "X-2FA-Code": twoFactorCode },
      timeout: 300000, // 5 min — restores longos
    }),
};

// ═══════════════════════════════════════════════════════════════════════════
// INFORMES DE ESTADO (condition reports)
// ═══════════════════════════════════════════════════════════════════════════
export const conditionReportService = {
  list: (reservationId) => api.get(`/reservations/${reservationId}/condition`),
  create: (reservationId, data) => api.post(`/reservations/${reservationId}/condition`, data),
};

// ═══════════════════════════════════════════════════════════════════════════
// ORGANIZACIÓNS (COOPERATIVAS / EMPRESAS)
// ═══════════════════════════════════════════════════════════════════════════
export const organizationService = {
  mine: () => api.get("/organizations/mine"),
  get: (id) => api.get(`/organizations/${id}`),
  create: (data) => api.post("/organizations", data),
  update: (id, data) => api.put(`/organizations/${id}`, data),
  delete: (id) => api.delete(`/organizations/${id}`),
  addMember: (id, email, role) => api.post(`/organizations/${id}/members`, { email, role }),
  updateMember: (id, userId, role) => api.put(`/organizations/${id}/members/${userId}`, { role }),
  removeMember: (id, userId) => api.delete(`/organizations/${id}/members/${userId}`),
};

// ═══════════════════════════════════════════════════════════════════════════
// CLAVES API
// ═══════════════════════════════════════════════════════════════════════════
export const apiKeyService = {
  list: () => api.get("/apikeys"),
  scopes: () => api.get("/apikeys/scopes"),
  create: (data) => api.post("/apikeys", data),
  revoke: (id) => api.delete(`/apikeys/${id}`),
};

// ═══════════════════════════════════════════════════════════════════════════
// MAPA
// ═══════════════════════════════════════════════════════════════════════════
export const mapService = {
  // Pins para o mapa (máquinas + fincas con lat/lng)
  pins: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ""))
    ).toString();
    return api.get(`/map/pins${qs ? `?${qs}` : ""}`);
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// FACTURAS (PDF)
// ═══════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════
// REPORTS (Denuncias de anuncios — Capa 3 antifraude)
// ═══════════════════════════════════════════════════════════════════════════
export const reportService = {
  // Endpoint público (non require auth, pero envíaa se está dispoñible)
  create: (payload) => api.post("/reports", payload),
};

// ═══════════════════════════════════════════════════════════════════════════
// ENTREGA A DOMICILIO
// ═══════════════════════════════════════════════════════════════════════════
export const deliveryService = {
  calculate: (machineId, destinationAddress, coords) =>
    api.post("/delivery/calculate", { machineId, destinationAddress, destinationLat: coords?.lat ?? undefined, destinationLng: coords?.lng ?? undefined }),
};

export const invoiceService = {
  downloadReservation: (reservationId) =>
    api.getBinary(`/invoices/reservation/${reservationId}`),

  downloadFinca: (fincaReservationId) =>
    api.getBinary(`/invoices/finca/${fincaReservationId}`),

  // Factura de comisión da plataforma (Cassiterion, S.L.U. → Propietario) — mensual.
  // Deducible polo propietario no seu IRPF/IVA.
  commissionMonths: () => api.get("/invoices/commission"),
  downloadCommission: (year, month) =>
    api.getBinary(`/invoices/commission/${year}/${month}`),
};

// ═══════════════════════════════════════════════════════════════════════════
// LIBRO REXISTRO (IRPF)
// ═══════════════════════════════════════════════════════════════════════════
export const leaseRegistryService = {
  download: (year, format = "csv") =>
    api.getBinary(`/lease-registry?year=${year}&format=${format}`),
};

// ═══════════════════════════════════════════════════════════════════════════
// PERFIL DE USUARIO
// ═══════════════════════════════════════════════════════════════════════════
export const userService = {
  // Perfil público dun usuario
  getPublic: (id) => api.get(`/auth/users/${id}`),

  // Actualizar o meu perfil
  updateMe: (data) => api.put("/auth/me", data),

  // Cambiar contrasinal
  changePassword: (currentPassword, newPassword) =>
    api.put("/auth/me/password", { currentPassword, newPassword }),

  // Reseñas dun usuario (como propietario ou arrendatario)
  reviews: (userId, type) => api.get(`/reviews/user/${userId}${type ? `?type=${type}` : ""}`),

  // Actualizar número ROPO
  updateRopo: (ropoNumber) => api.put("/auth/me/ropo", { ropoNumber }),

  // Establecer/cambiar réxime fiscal (PARTICULAR | AUTONOMO_GENERAL | AUTONOMO_REAGP | EMPRESA)
  setVatRegime: (vatRegime, accepted = true) =>
    api.put("/auth/me/vat-regime", { vatRegime, accepted }),

  // Intención declarada no onboarding (RENTER | OWNER | BOTH)
  setIntent: (intent) => api.put("/auth/me/intent", { intent }),
};

// ═══════════════════════════════════════════════════════════════════════════
// ACCOUNTANTS (asesor fiscal compartido — Pro)
// ═══════════════════════════════════════════════════════════════════════════
export const accountantService = {
  // Owner: xestión de asesores
  invite:       (email)       => api.post("/accountants/invite", { email }),
  myAdvisors:   ()            => api.get("/accountants/my-advisors"),
  revokeLink:   (linkId)      => api.delete(`/accountants/links/${linkId}`),
  revokeInvite: (inviteId)    => api.post(`/accountants/invites/${inviteId}/revoke`),
  sendQuarterly: (accountantEmail, year, quarter) =>
    api.post("/accountants/send-quarterly", { accountantEmail, year, quarter }),

  // Asesor: acceptar invite + listar clientes
  accept:       (token)       => api.post("/accountants/accept", { token }),
  myClients:    ()            => api.get("/accountants/my-clients"),
};

// ═══════════════════════════════════════════════════════════════════════════
// INCOME (panel "Ingresos" do propietario)
// ═══════════════════════════════════════════════════════════════════════════
//
// Helper: se a URL actual ten `?asClient=ID` (modo asesor), inxecta ese
// parámetro automaticamente nos endpoints de income para que o backend
// resolva a impersonación.
function withAsClient(params = {}) {
  if (typeof window === "undefined") return params;
  try {
    const asClient = new URLSearchParams(window.location.search).get("asClient");
    if (asClient && !params.asClient) return { ...params, asClient };
  } catch { /* noop */ }
  return params;
}

export const incomeService = {
  ownerSummary: () => {
    const qs = new URLSearchParams(withAsClient()).toString();
    return api.get(`/income/owner${qs ? `?${qs}` : ""}`);
  },

  // Libro de Ingresos (Pro). Devolve 402 se o user non ten plan Pro/Business.
  ledger: (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(
      Object.entries(withAsClient(params)).filter(([, v]) => v != null && v !== "")
    )).toString();
    return api.get(`/income/owner/ledger${qs ? `?${qs}` : ""}`);
  },
  // Libro de IVE (Pro): agregado trimestral con repercutido + soportado
  vatBook: (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(
      Object.entries(withAsClient(params)).filter(([, v]) => v != null && v !== "")
    )).toString();
    return api.get(`/income/owner/vat-book${qs ? `?${qs}` : ""}`);
  },
  exportVatBook: async (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(
      Object.entries(withAsClient({ ...params, format: "csv" })).filter(([, v]) => v != null && v !== "")
    )).toString();
    const res = await fetch(`${import.meta.env.VITE_API_URL || ""}/income/owner/vat-book/export?${qs}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("accessToken") || ""}` },
    });
    if (!res.ok) {
      let body; try { body = await res.json(); } catch { body = null; }
      const err = new Error(body?.message || body?.error || `HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }
    const blob = await res.blob();
    const filename = (res.headers.get("Content-Disposition") || "")
      .match(/filename="?([^"]+)"?/)?.[1] || "libro-ive.csv";
    return { blob, filename };
  },

  // Modelos AEAT (Pro): borradores pre-rechinados.
  aeatForm: (model, params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(
      Object.entries(withAsClient(params)).filter(([, v]) => v != null && v !== "")
    )).toString();
    return api.get(`/income/owner/aeat/${model}${qs ? `?${qs}` : ""}`);
  },
  // Conciliación bancaria (Pro)
  reconciliation: (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(
      Object.entries(withAsClient(params)).filter(([, v]) => v != null && v !== "")
    )).toString();
    return api.get(`/income/owner/reconciliation${qs ? `?${qs}` : ""}`);
  },
  exportReconciliation: async (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(
      Object.entries(withAsClient({ ...params, format: "csv" })).filter(([, v]) => v != null && v !== "")
    )).toString();
    const res = await fetch(`${import.meta.env.VITE_API_URL || ""}/income/owner/reconciliation/export?${qs}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("accessToken") || ""}` },
    });
    if (!res.ok) {
      let body; try { body = await res.json(); } catch { body = null; }
      const err = new Error(body?.message || body?.error || `HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }
    const blob = await res.blob();
    const filename = (res.headers.get("Content-Disposition") || "")
      .match(/filename="?([^"]+)"?/)?.[1] || "conciliacion.csv";
    return { blob, filename };
  },

  // Descarga PDF dun modelo AEAT (mesma forma que CSV pero content-type PDF).
  exportAeatFormPdf: async (model, params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(
      Object.entries(withAsClient(params)).filter(([, v]) => v != null && v !== "")
    )).toString();
    const res = await fetch(`${import.meta.env.VITE_API_URL || ""}/income/owner/aeat/${model}/pdf?${qs}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("accessToken") || ""}` },
    });
    if (!res.ok) {
      let body; try { body = await res.json(); } catch { body = null; }
      const err = new Error(body?.message || body?.error || `HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }
    const blob = await res.blob();
    const filename = (res.headers.get("Content-Disposition") || "")
      .match(/filename="?([^"]+)"?/)?.[1] || `modelo-${model}.pdf`;
    return { blob, filename };
  },

  exportAeatForm: async (model, params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(
      Object.entries(withAsClient({ ...params, format: "csv" })).filter(([, v]) => v != null && v !== "")
    )).toString();
    const res = await fetch(`${import.meta.env.VITE_API_URL || ""}/income/owner/aeat/${model}/export?${qs}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("accessToken") || ""}` },
    });
    if (!res.ok) {
      let body; try { body = await res.json(); } catch { body = null; }
      const err = new Error(body?.message || body?.error || `HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }
    const blob = await res.blob();
    const filename = (res.headers.get("Content-Disposition") || "")
      .match(/filename="?([^"]+)"?/)?.[1] || `modelo-${model}.csv`;
    return { blob, filename };
  },

  // Descarga directa do CSV — devuelve Blob
  exportLedger: async (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(
      Object.entries(withAsClient({ ...params, format: "csv" })).filter(([, v]) => v != null && v !== "")
    )).toString();
    const res = await fetch(`${import.meta.env.VITE_API_URL || ""}/income/owner/ledger/export?${qs}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("accessToken") || ""}` },
    });
    if (!res.ok) {
      let body;
      try { body = await res.json(); } catch { body = null; }
      const err = new Error(body?.message || body?.error || `HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }
    const blob = await res.blob();
    const filename = (res.headers.get("Content-Disposition") || "")
      .match(/filename="?([^"]+)"?/)?.[1] || "libro-ingresos.csv";
    return { blob, filename };
  },
};

// ─── Subscriptions (Pro / Business) ────────────────────────────────────────
// O backend xa fai bypass automático para emails en ADMIN_EMAILS,
// devolvendo {plan: "BUSINESS", isAdminBypass: true}.
export const subscriptionService = {
  me: () => api.get("/subscriptions/me"),
  checkout: (body) => api.post("/subscriptions/checkout", body),
  portal: () => api.post("/subscriptions/portal", {}),
};

// ─── Axudas e subvencións ──────────────────────────────────────────────────
export const axudasService = {
  // Body: { subsidyTitle, name, email, phone?, message? }
  consulta: (body) => api.post("/axudas/consulta", body),
};

// ─── GDPR (Art. 15 acceso, Art. 17 supresión) ─────────────────────────────
export const gdprService = {
  // Devolve o JSON con todos os datos do usuario.
  exportData: () => api.get("/gdpr/export"),
  // Borra/anonimiza a conta. Body: { confirm: true, password: <actual> }.
  deleteAccount: (password) =>
    api.delete("/gdpr/account", { data: { confirm: true, password } }),
};
